// src/features/auth/authSlice.js

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api, { getAuthToken, normalizeApiError, refreshAuthSession, setHeaderToken } from "../system/api";
import { clear, getUser, setUser } from "../system/storage";

const loginUser = async (identifier, password, Captcha) => {
    const headerConfig = {
        headers: { "Content-Type": "application/json" },
        withCredentials: true
    };
    try {
        const { data:{user, jwt, refreshToken, deviceId, session} } = await api.post('/auth/local', { identifier, password, Captcha }, headerConfig);
        return { user: {...user, jwt, refreshToken, deviceId, session}, message: "login-successfully" };
    } catch (err) {
        throw new Error(normalizeApiError(err).message);
    }
};

export const exchangeExternalJwt = async (provider, token) => {
    const response = await api.post("/auth/external", {provider, token}, {
        headers: {"Content-Type": "application/json"},
        suppressGlobalErrorHandler: true,
    });
    const {user, jwt, refreshToken, deviceId, session} = response.data || {};
    if (!user || !jwt) throw new Error("external-auth.error.invalid-response");
    return {...user, jwt, refreshToken, deviceId, session, externalProvider: provider};
};

const loginOtp = async (Username, Mobile, Email, Channel, Captcha) => {
    const headerConfig = {
        headers: { "Content-Type": "application/json" },
        withCredentials: true
    };
    try {

        const { data } = await api.post('/User/CheckUser', { Username, Mobile, Email, Channel, Captcha }, headerConfig);
        return { data, message: "otp-sent-successfully" };
    } catch (err) {
        throw new Error(normalizeApiError(err).message);
    }
};
const checkOtp = async (Username, Mobile, Email, Channel, VerifyCode, Captcha) => {
    const headerConfig = {
        headers: { "Content-Type": "application/json" },
        withCredentials: true
    };
    try {
        const { data } = await api.post('/User/ConfirmUser', { Username, Mobile, Email, Channel, VerifyCode:VerifyCode.toUpperCase(), Captcha }, headerConfig);
        return { data, message: "otp-verified-successfully" };
    } catch (err) {
        throw new Error(normalizeApiError(err).message);
    }
};

const signupUser = async (Username, Password, Captcha) => {
    const headerConfig = {
        headers: { "Content-Type": "application/json" },
        withCredentials: true
    };
    try {
        const { data } = await api.post('/User/CheckUser', {Username, Password, Captcha}, headerConfig);
        return { data, message: "signup-successfully" };
    } catch (err) {
        throw new Error(normalizeApiError(err).message);
    }
};

export const logoutUser = async (user, remote = true) => {
    if (remote) {
        const storedUser = getUser();
        let token = getAuthToken(user) || getAuthToken(storedUser);
        let deviceId = user?.deviceId || storedUser?.deviceId;

        if (!token && storedUser?.refreshToken) {
            const refreshedUser = await refreshAuthSession();
            token = getAuthToken(refreshedUser);
            deviceId = refreshedUser?.deviceId || deviceId;
        }
        if (!token) throw new Error("security.logout-failed");

        await api.post('/auth/logout', {deviceId}, {
            headers: {Authorization: `Bearer ${token}`},
            suppressGlobalErrorHandler: true,
        });
    }

    clear();
};

const applyAuthToken = (user) => {
    const token = getAuthToken(user);
    setHeaderToken(token ? `Bearer ${token}` : "");
};

// --------- THUNKS ---------

export const login = createAsyncThunk('auth/login', async ({ identifier, password, Captcha }, thunkAPI) => {
    try {
        const { user, message } = await loginUser(identifier, password, Captcha);
        // const { value } = await checkUser();
        if (user) {
            let userData = {...user};
            setUser(userData);
            return userData;
        } else {
            return thunkAPI.rejectWithValue(message);
        }
    } catch (err) {
        return thunkAPI.rejectWithValue(err.message);
    }
});


export const sendOTP = createAsyncThunk('auth/sendOTP', async ({ Username, Mobile, Email, Channel, Captcha }, thunkAPI) => {
    try {
        const { data, message } = await loginOtp(Username, Mobile, Email, Channel, Captcha);

        // console.log(data, message)

        if (data) {
            // let userData = { ...data, ...value[0] };

            return data;
        } else {
            return thunkAPI.rejectWithValue(message);
        }
    } catch (err) {
        return thunkAPI.rejectWithValue(err.message);
    }
});

export const confirmOtp = createAsyncThunk('auth/confirmOtp', async ({ Username, Mobile, Email, Channel, VerifyCode, Captcha }, thunkAPI) => {
    try {
        const { data, message } = await checkOtp(Username, Mobile, Email, Channel, VerifyCode, Captcha);
        applyAuthToken(data);
        if (data) {
            let userData = {...data};
            setUser(userData);
            return userData;
        } else {
            return thunkAPI.rejectWithValue(message);
        }
    } catch (err) {
        return thunkAPI.rejectWithValue(err.message);
    }
});

export const signup = createAsyncThunk('auth/signup', async ({Username, Password, Captcha}, thunkAPI) => {
    try {
        const { data, message } = await signupUser(Username, Password, Captcha);
        applyAuthToken(data);
        // const { value } = await checkUser();

        if (data) {
            let userData = { ...data };
            setUser(userData);
            return userData;
        } else {
            return thunkAPI.rejectWithValue(message);
        }
    } catch (err) {
        return thunkAPI.rejectWithValue(err.message);
    }
});

export const check = createAsyncThunk('auth/check', async (payload, thunkAPI) => {
    try {
        let user = getUser();
        // const { value } = await checkUser();
        // user = { ...user };
        // setUser(user);

        return user;
    } catch (err) {
        console.log(err);
        return thunkAPI.rejectWithValue(err.message);
    }
});

export const logout = createAsyncThunk('auth/logout', async (options = {}, thunkAPI) => {
    try {
        await logoutUser(thunkAPI.getState().auth.user, options?.remote !== false);
    } catch (error) {
        thunkAPI.dispatch({
            type: "app/setMessage",
            payload: {severity: "error", content: "security.logout-failed"},
        });
        return thunkAPI.rejectWithValue(normalizeApiError(error).message);
    }
});

/** Exchanges a JWT issued by a configured trusted service for a local app session. */
export const loginExternal = createAsyncThunk("auth/loginExternal", async ({provider, token}, thunkAPI) => {
    try {
        const user = await exchangeExternalJwt(provider, token);
        setUser(user);
        return user;
    } catch (error) {
        return thunkAPI.rejectWithValue(normalizeApiError(error).message);
    }
});

export const generateCaptcha = async () => {
    try {
        const headerConfig = { withCredentials: true };
        const data = await api.post(`User/GenerateCaptcha`, null, headerConfig);
        return data.data.captchaImage;
    } catch (e) {
        throw e;
    }
};

// --------- SLICE ---------

const authSlice = createSlice({
    name: 'auth',
    initialState: {
        user: getUser(),
        isAuthenticated: false,
        userChecked: false,
        loading: false,
        error: null,
        otpSent:undefined

    },
    reducers: {
        setOtpSent:(state, action)=>{
            state.otpSent = action.payload;
        },
        saveUser:(state, action)=>{
            state.user = action.payload;
            state.isAuthenticated = true;
            applyAuthToken(action.payload);
            setUser(action.payload);
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(loginExternal.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(loginExternal.fulfilled, (state, action) => {
                state.loading = false;
                state.user = action.payload;
                state.isAuthenticated = true;
                applyAuthToken(state.user);
            })
            .addCase(loginExternal.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // login
            .addCase(login.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(login.fulfilled, (state, action) => {
                state.loading = false;
                state.user = action.payload;
                state.isAuthenticated = true;
                applyAuthToken(state.user);
            })
            .addCase(login.rejected, (state, action) => {

                state.loading = false;
                state.error = action.payload;
            })
            .addCase(sendOTP.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(sendOTP.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.otpSent = action.payload.value;
            })
            .addCase(sendOTP.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                state.otpSent = undefined;
            })
            .addCase(confirmOtp.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(confirmOtp.fulfilled, (state, action) => {
                state.loading = false;
                state.user = action.payload;
                state.isAuthenticated = true;
                state.otpSent = undefined;
                applyAuthToken(state.user);
            })
            .addCase(confirmOtp.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                state.otpSent = undefined;
            })

            // signup
            .addCase(signup.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(signup.fulfilled, (state, action) => {
                state.loading = false;
                state.user = action.payload;
                state.isAuthenticated = true;
                applyAuthToken(state.user);
            })
            .addCase(signup.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // check
            .addCase(check.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(check.fulfilled, (state, action) => {
                state.loading = false;
                state.user = action.payload;
                state.isAuthenticated = state.user!==null;
                state.userChecked = true;
            })
            .addCase(check.rejected, (state) => {
                state.loading = false;
                state.user = null;
                state.error = null;
                state.isAuthenticated = false;
                state.userChecked = true;
            })

            // logout
            .addCase(logout.fulfilled, (state) => {
                state.user = null;
                state.isAuthenticated = false;
                state.error = null;
                setHeaderToken("");
            })
            .addCase(logout.rejected, (state, action) => {
                state.error = action.payload || "security.logout-failed";
            });
    }
});
export const { setOtpSent, saveUser } = authSlice.actions;

export default authSlice.reducer;
